import {
  faFilePdf,
  faLandmark,
  faMagnifyingGlassLocation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { awaitsConfirmation, euro, ownershipSummary, quotedParcels } from "./actionRuns";
import { useWorkspace } from "./store";
import { ActionRun } from "./types";

// Het antwoord opslaan als PDF: wacht op het afgemaakte antwoord.
const SavedDocument = ({ run }: { run: ActionRun }) => (
  <p>
    <FontAwesomeIcon icon={faFilePdf} size="sm" className="mr-1" />
    {run.status === "done" && run.fileUrl ? (
      <a href={run.fileUrl} target="_blank" rel="noreferrer" className="underline">
        {run.fileName}
      </a>
    ) : run.status === "pending" ? (
      <span className="text-gray-600">Wordt opgeslagen zodra het antwoord af is…</span>
    ) : (
      <span className="text-red-600">{run.error || "Niet opgeslagen."}</span>
    )}
  </p>
);

// Eigendom opvragen: betaald, dus eerst het bedrag en een klik.
const Ownership = ({ run }: { run: ActionRun }) => {
  const confirmAction = useWorkspace((s) => s.confirmAction);
  const cancelAction = useWorkspace((s) => s.cancelAction);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const act = async (action: (id: number) => Promise<string[] | undefined>) => {
    setBusy(true);
    setErrors((await action(run.id)) ?? []);
    setBusy(false);
  };
  const parcels = quotedParcels(run);

  return (
    <div>
      <p className="font-bold">
        <FontAwesomeIcon icon={faLandmark} size="sm" className="mr-1" />
        Eigendomsinformatie · {parcels.join(", ")}
      </p>
      {run.status === "done" && <p className="mt-0.5">{ownershipSummary(run.result)}</p>}
      {run.status === "cancelled" && <p className="mt-0.5 text-gray-500">Geannuleerd.</p>}
      {run.status === "running" && <p className="mt-0.5 text-gray-600">Bezig met opvragen…</p>}
      {run.status === "failed" && run.error && <p className="mt-0.5 text-red-600">{run.error}</p>}
      {awaitsConfirmation(run) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => act(confirmAction)}
            className="rounded bg-bright-yellow px-2.5 py-1 font-bold hover:bg-yellow disabled:opacity-50"
          >
            {busy ? "Opvragen…" : `Opvragen voor ${euro(run.costEur)}`}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => act(cancelAction)}
            className="px-2 py-1 text-gray-500"
          >
            Annuleren
          </button>
          <span className="text-[11px] text-gray-500">Betaald: Kadaster, per perceel.</span>
        </div>
      )}
      {errors.length > 0 && <p className="mt-1 text-red-600">{errors.join("; ")}</p>}
    </div>
  );
};

// Een gestart kavelonderzoek: de voortgang staat in het onderzoek zelf.
const Research = ({
  run,
  onOpenResearch,
}: {
  run: ActionRun;
  onOpenResearch?: (researchId: number) => void;
}) => (
  <div className="flex flex-wrap items-center gap-2">
    <p className="font-bold">
      <FontAwesomeIcon icon={faMagnifyingGlassLocation} size="sm" className="mr-1" />
      Kavelonderzoek {run.arguments.parcel?.name} gestart
    </p>
    {run.researchId && (
      <button
        type="button"
        onClick={() => onOpenResearch?.(run.researchId!)}
        className="rounded border border-yellow px-2.5 py-1 font-bold hover:bg-yellow/40"
      >
        Bekijk onderzoek
      </button>
    )}
  </div>
);

// Een filter staat al als filterknop bij het antwoord en een gespreksrapport als
// eigen kaart; die tonen hun record.
const SHOWN_ELSEWHERE = ["apply_filter", "conversation_report"];

export const hasCard = (run: ActionRun) => !SHOWN_ELSEWHERE.includes(run.action);

/** Wat een skill deed of aanvroeg, als kaart onder het antwoord. */
export const ActionRunCard = ({
  run,
  onOpenResearch,
}: {
  run: ActionRun;
  onOpenResearch?: (researchId: number) => void;
}) =>
  hasCard(run) ? (
    <div className="mt-1.5 rounded border border-yellow/60 bg-white p-1.5">
      {run.action === "request_ownership" ? (
        <Ownership run={run} />
      ) : run.action === "save_document" ? (
        <SavedDocument run={run} />
      ) : run.action === "start_research" ? (
        <Research run={run} onOpenResearch={onOpenResearch} />
      ) : (
        <p>{run.label}</p>
      )}
    </div>
  ) : null;
